import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { useResolvedUser, useFeatureFlags, useProductAssetsByUser } from '@tcc/shared-data-lib';
import { initializeAnalytics, DD } from '@tcc/analytics';
import { AlertToaster, useBreakpoint } from '@tcc/fds';
import { ErrorFallback } from '../components/ErrorFallback';
import { FirstLoginVideoDialog } from '../components/FirstLoginVideoDialog/FirstLoginVideoDialog';
import { SpinnerLoading } from '../components/SpinnerLoading/SpinnerLoading';
import { UnverifiedBlocker } from '../components/UnverifiedBlocker';
import { AskFieldviewWebPluginContainer } from '../containers/AskFieldviewWebPluginContainer';
import { ConnectPluginContainer } from '../containers/ConnectPluginContainer';
import { CropPlanningPluginContainer } from '../containers/CropPlanningPluginContainer';
import { DashboardPluginContainer } from '../containers/DashboardPluginContainer';
import { DataHubPluginContainer } from '../containers/DataHubPluginContainer';
import { DataInboxPluginContainer } from '../containers/DataInboxPluginContainer';
import { DmbPluginContainer } from '../containers/DmbPluginContainer';
import { FieldSelectionPluginContainer } from '../containers/FieldSelectionContainer';
import { FixitToolPluginContainer } from '../containers/FixitToolPluginContainer';
import { InfoArchitecturePluginContainer } from '../containers/InfoArchitecturePluginContainer';
import { ModuleFederationPluginContainer } from '../containers/ModuleFederationPluginContainer';
import { ProgramsPluginContainer } from '../containers/ProgramsPluginContainer';
import { ReportsPluginContainer } from '../containers/ReportsPluginContainer';
import { SeasonReportsPluginContainer } from '../containers/SeasonReportsContainer';
import { VariableRatePluginContainer } from '../containers/VariableRateContainer';
import { FusePluginContainer } from '../containers/FusePluginContainer';
import { useHistoryListener } from '../hooks/useHistoryListener';
import { useOperations } from '../hooks/useOperations/useOperations';
import { useSessionSettings } from '../hooks/useSessionSettings';
import { useApplicationViewLoadVital } from '../hooks/useApplicationViewLoadVital';
import { getUserData, reAuthenticate } from '../utils/wam';

import { ExpertoDialog } from '../components/ExpertoDialog/ExpertoDialog';
import { ProxyBanner } from '../components/ProxyBanner/ProxyBanner';
import { ROUTE } from '../constants/routes';
import { SessionSetting } from '../constants/ui';
import { InSeasonPluginContainer } from '../containers/InSeasonContainer';
import { OperationHubPluginContainer } from '../containers/OperationHubPluginContainer';
import { ScriptCreatorPluginContainer } from '../containers/ScriptCreatorContainer';
import { SettingsPluginContainer } from '../containers/SettingsPluginContainer/SettingsPluginContainer';
import { useCropPlanningAccess } from '../contexts/CropPlanningAccessContext';
import { useCobrowsingSupport } from '../hooks/useCobrowsingSupport';
import { useExpertoBanner } from '../hooks/useExpertoBanner';
import { useFirstLoginVideo } from '../hooks/useFirstLoginVideo';
import { useMarketingNotifications } from '../hooks/useMarketingNotifications';
import { getAvailableFeatures } from '../utils/getAvailableFeatures';
import { isDealerImpersonatingFarmer } from '../utils/isImpersonating';
import { isInternalUser } from '../utils/isInternalUser';
import {
  selectUserByProxyPreference,
  useShouldUseProxyDataSourcePreference,
  useFeatureFlagsConfigs,
  useProductAssetsByUserConfigs,
  useUserLoggedInConfigs,
  withUseQueryConfig,
} from '../utils/sharedData';
import { AuthorizedRoute } from './AuthorizedRoute';
import { MFADialog } from '../components/MFADialog/MFADialog';
import { UserRole } from '../types/user';
import styles from './styles.css';

export const ApplicationView = () => {
  const { clientId } = getUserData();
  const location = useLocation();
  const {
    operations,
    operationsSelected,
    setSelectedOperations,
  } = useOperations(clientId);

  const { isDesktop } = useBreakpoint();
  const { sessionSettings, updateSessionSetting } = useSessionSettings();
  const [pluginContainerElement, setPluginContainerElement] = useState<HTMLDivElement | null>(null);

  const getInitialExpandedState = () => {
    const storedValue = sessionSettings[SessionSetting.SidenavExpanded];
    return storedValue !== null ? storedValue : isDesktop;
  };

  const [expanded, setExpandedState] = useState(getInitialExpandedState);

  const setExpanded = (value: boolean) => {
    setExpandedState(value);
    updateSessionSetting(SessionSetting.SidenavExpanded, value);
  };

  const [isExpertoDialogOpen, setIsExpertoDialogOpen] = useState(false);
  const handleOpenExpertoDialog = useCallback(() => setIsExpertoDialogOpen(true), []);
  const handleCloseExpertoDialog = useCallback(() => setIsExpertoDialogOpen(false), []);
  const handlePluginContainerRef = useCallback((node: HTMLDivElement | null) => {
    setPluginContainerElement(node);
  }, []);

  const {
    user,
    loggedInUser,
    impersonatedUser,
    isProxying,
    isLoading: isLoadingUser,
  } = useResolvedUser(useUserLoggedInConfigs);
  const shouldUseProxyDataSource = useShouldUseProxyDataSourcePreference();
  const selectedSharedDataUser = useMemo(
    () => selectUserByProxyPreference(
      {
        user,
        loggedInUser,
        impersonatedUser,
        isProxying,
      },
      shouldUseProxyDataSource,
    ),
    [
      user,
      loggedInUser,
      impersonatedUser,
      isProxying,
      shouldUseProxyDataSource,
    ],
  );
  const userId = user?.id?.toString() || '';
  const { featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags(
    useFeatureFlagsConfigs(selectedSharedDataUser),
  );

  const useProductAssetsByUserArgs = useProductAssetsByUserConfigs(selectedSharedDataUser);
  const { data: assets, isLoading: isLoadingAssets } = useProductAssetsByUser(useProductAssetsByUserArgs);
  useProductAssetsByUser(withUseQueryConfig(useProductAssetsByUserArgs));
  const { canAccess: canAccessCropPlanning, isLoading: isLoadingCropPlanningAccess } = useCropPlanningAccess();
  const {
    isOpen: isFirstLoginVideoDialogOpen,
    handleClose: handleCloseFirstLoginVideo,
  } = useFirstLoginVideo({
    user,
    featureFlags,
    isUserLoading: isLoadingUser,
    isFeatureFlagsLoading: isLoadingFeatureFlags,
  });

  // Loading gate: while true we render <SpinnerLoading />, once false the real page renders.
  const isMissingRequiredData = !user || !featureFlags;
  const isSharedDataLoading = isLoadingAssets || isLoadingFeatureFlags;
  const isAppLoading = isMissingRequiredData || isSharedDataLoading;

  // Track how long the shared-data loading spinner is shown before the app renders.
  useApplicationViewLoadVital(isAppLoading);

  useEffect(() => {
    if (user && user.details) {
      initializeAnalytics({ userDetails: user.details, featureFlags });
      if (userId) {
        DD.setUser({ id: userId as string });
        // Only a derived boolean is sent to RUM so internal sessions can be counted/filtered.
        // The raw email is evaluated locally and never attached to sessions or events.
        DD.setGlobalContextProperty('is_internal_user', isInternalUser(user.details.email));
      }
    }
  }, [featureFlags, user, userId]);

  // TODO: remove useHistoryListener prop after FEWEB-1305
  useHistoryListener(featureFlags?.['web.analytics.monitoring']);
  useMarketingNotifications();
  useCobrowsingSupport();
  const { handleConfirmHideExpertoBanner } = useExpertoBanner({
    user,
    featureFlags: featureFlags || {},
    containerElement: pluginContainerElement,
    onOpenDialog: handleOpenExpertoDialog,
    onCloseDialog: handleCloseExpertoDialog,
  });

  const operationsSelectedIds = useMemo(
    () => operationsSelected.filter((op) => op != null).map((op) => op.operationId),
    [operationsSelected],
  );

  const blockUser = user?.details?.emailVerificationStatus === 'UNVERIFIED';
  if (blockUser) {
    return <UnverifiedBlocker onLogout={reAuthenticate} timeout={5000} />;
  }

  if (isAppLoading) {
    return <SpinnerLoading />;
  }

  const availableFeatures = getAvailableFeatures({ assets, featureFlags });
  const Home = availableFeatures.dashboard ? ROUTE.Dashboard : ROUTE.Home;
  const mfaBaseUser = loggedInUser || user;
  const resolvedMfaUser = impersonatedUser && mfaBaseUser?.details?.roles?.includes(UserRole.SUPPORT)
    ? impersonatedUser
    : mfaBaseUser;

  return (
    <ErrorBoundary
      fallback={<ErrorFallback />}
      onError={(error, info) => {
        // Datadog RUM may not be available (SDK not loaded/initialized, or DD
        // stubbed out). Guard the call and never let reporting throw on its own.
        try {
          DD?.addError?.(error, {
            source: 'react-error-boundary',
            componentStack: info.componentStack,
          });
        } catch {
          // Swallow — failing to report must not mask the original crash.
        }
      }}
    >
      <AlertToaster />
      <main className={`${styles.mainContainer} ${styles.mainContainerRow}`}>
        <FirstLoginVideoDialog
          isOpen={isFirstLoginVideoDialogOpen}
          onClose={handleCloseFirstLoginVideo}
        />
        {resolvedMfaUser && (
          <MFADialog
            user={resolvedMfaUser}
            featureFlags={featureFlags}
          />
        )}
        <ProxyBanner />
        <InfoArchitecturePluginContainer
          expanded={expanded}
          operations={operations}
          operationsSelected={operationsSelected}
          setExpandedState={setExpanded}
          setSelectedOperations={setSelectedOperations}
        />
        <div className={styles.pluginWrapper}>
          <div className={styles.pluginContainer} ref={handlePluginContainerRef}>
            <Suspense fallback={<SpinnerLoading />}>
              <ExpertoDialog
                isOpen={isExpertoDialogOpen}
                onClose={handleCloseExpertoDialog}
                onOpenChange={setIsExpertoDialogOpen}
                onPrimaryAction={handleConfirmHideExpertoBanner}
              />
              <Switch>
                <AuthorizedRoute
                  isEnabled={availableFeatures.reports}
                  path={ROUTE.Reports}
                  render={() => <ReportsPluginContainer operationsSelected={operationsSelected} />}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.seasonReports}
                  path={ROUTE.SeasonReports}
                  render={() => (
                    <SeasonReportsPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )}
                />
                <Route path={ROUTE.DataInbox} render={() => <DataInboxPluginContainer operationsSelected={operationsSelected} />} />
                <Route path={ROUTE.FixitTool} render={() => <FixitToolPluginContainer operationsSelected={operationsSelected} />} />
                <Route
                  path={ROUTE.Dmb}
                  render={() => (
                    <DmbPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.VariableRate}
                  render={() => (
                    <VariableRatePluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.FieldSelection}
                  render={() => (
                    <section className={styles.flexFSWrapper}>
                      <FieldSelectionPluginContainer
                        operationsSelected={operationsSelected}
                      />
                    </section>
                  )}
                />
                <Route path={ROUTE.ScriptCreator} render={() => (
                  <section>
                    <ScriptCreatorPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  </section>
                )} />
                <Route path={ROUTE.Settings} render={() => (
                  <SettingsPluginContainer
                    operations={operations}
                    operationsSelected={operationsSelectedIds}
                  />
                )} />
                <Route path={ROUTE.ModuleFederation} render={() => (
                  <ModuleFederationPluginContainer />
                )} />
                <AuthorizedRoute
                  isEnabled={availableFeatures.programs}
                  path={ROUTE.Programs}
                  component={ProgramsPluginContainer}
                />
                {/* TODO ADO-12609: Remove this redirect - data-hub was never released */}
                <Route exact path="/data-hub">
                  <Redirect to="/analytics" />
                </Route>
                <AuthorizedRoute
                  isEnabled={availableFeatures.analytics}
                  path={ROUTE.DataHub}
                  render={() => (
                    <DataHubPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.dashboard}
                  path={ROUTE.Dashboard}
                  render={() => (
                    <DashboardPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelected}
                    />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.connect}
                  path={ROUTE.Partners}
                  component={ConnectPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={canAccessCropPlanning && !isDealerImpersonatingFarmer(user, impersonatedUser)}
                  isLoading={isLoadingCropPlanningAccess}
                  path={ROUTE.CropPlanning}
                  component={CropPlanningPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.askFieldView}
                  path={ROUTE.AskFieldView}
                  component={AskFieldviewWebPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.inSeason}
                  path={ROUTE.InSeason}
                  render={() => (
                    <InSeasonPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.operationHub}
                  path={ROUTE.OperationHub}
                  component={OperationHubPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.fuse}
                  path={ROUTE.Fuse}
                  render={() => (
                    <FusePluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <Route path="*">
                  <Redirect to={{
                    ...location,
                    pathname: Home
                  }} />
                </Route>
              </Switch>
            </Suspense>
          </div>
        </div>
      </main>
    </ErrorBoundary>
  );
};
